/**
 * HorizontalBarChart - Reusable horizontal bar chart component.
 * Used for displaying ranked data like trigger sources, top users, etc.
 */
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
} from 'recharts'
import { chartColors, chartDefaults, tooltipStyles } from './chartConfig'
import { BarChart3 } from 'lucide-react'

/**
 * Generic data point interface with flexible keys
 */
export interface HorizontalBarDataPoint {
  [key: string]: string | number
}

export interface HorizontalBarChartProps<T extends HorizontalBarDataPoint> {
  /** Array of data points */
  data: T[]
  /** Key for category names */
  nameKey?: string
  /** Key for numeric values */
  valueKey?: string
  /** Chart height in pixels */
  height?: number
  /** Bar color */
  color?: string
  /** Accessible label for screen readers */
  ariaLabel?: string
  /** Whether to use gradient colors for bars */
  useGradient?: boolean
}

/**
 * Generate gradient colors for bars based on position
 */
function getGradientColor(index: number): string {
  const colors = [
    chartColors.primary,
    chartColors.info,
    chartColors.success,
    chartColors.warning,
    'hsl(280, 65%, 60%)', // Purple
    'hsl(190, 80%, 50%)', // Cyan
  ]
  return colors[index % colors.length]
}

/**
 * Reusable horizontal bar chart for displaying ranked/categorical data.
 * 
 * @example
 * ```tsx
 * // Trigger sources
 * <HorizontalBarChart 
 *   data={[{ name: 'WebUI', value: 150 }, { name: 'API', value: 100 }]}
 *   ariaLabel="Sessions by trigger source"
 * />
 * 
 * // Top users with custom keys
 * <HorizontalBarChart 
 *   data={[{ userId: 'user@example.com', sessionCount: 50 }]}
 *   nameKey="userId"
 *   valueKey="sessionCount"
 *   ariaLabel="Top users by session count"
 * />
 * ```
 */
export function HorizontalBarChart<T extends HorizontalBarDataPoint>({
  data,
  nameKey = 'name',
  valueKey = 'value',
  height = chartDefaults.height,
  color = chartColors.primary,
  ariaLabel = 'Horizontal bar chart showing categorical data',
  useGradient = true,
}: HorizontalBarChartProps<T>) {
  // Empty state
  if (!data || data.length === 0) {
    return (
      <div 
        className="flex flex-col items-center justify-center text-muted-foreground"
        style={{ height }}
      >
        <BarChart3 className="h-12 w-12 mb-3 opacity-50" />
        <p className="text-sm">No data available</p>
      </div>
    )
  }

  return (
    <div 
      role="img" 
      aria-label={ariaLabel}
      style={{ height }}
    >
      <ResponsiveContainer width="100%" height="100%" minHeight={1}>
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <XAxis 
            type="number"
            dataKey={valueKey}
            axisLine={false}
            tickLine={false}
            tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
          />
          <YAxis 
            type="category"
            dataKey={nameKey}
            width={120}
            axisLine={false}
            tickLine={false}
            tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
          />
          <Tooltip 
            contentStyle={tooltipStyles.contentStyle}
            labelStyle={tooltipStyles.labelStyle}
            cursor={{ fill: 'hsl(var(--muted))', opacity: 0.3 }}
          />
          <Bar 
            dataKey={valueKey} 
            radius={[0, 4, 4, 0]}
            maxBarSize={40}
          >
            {data.map((_, index) => (
              <Cell 
                key={`cell-${index}`}
                fill={useGradient ? getGradientColor(index) : color}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}
